import {
  buildPath,
  guidanceCanApply,
  type RerunUnapprovedResponse,
  type Sample,
  type StyleSheet,
} from "@contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../../api/fetcher";
import { queryKeys } from "../../api/keys";
import { AudioProvider, usePlayback } from "../../components/audio/AudioProvider";
import { Button } from "../../components/control/Button";
import { TextArea } from "../../components/control/TextArea";
import { TextInput } from "../../components/control/TextInput";
import { Card } from "../../components/data/Card";
import { Banner } from "../../components/feedback/Banner";
import { ErrorBanner } from "../../components/feedback/ErrorBanner";
import { ConfirmDialog } from "../../components/overlay/ConfirmDialog";
import { DraftActions } from "../../drafts/DraftActions";
import { useWorkspaceDraft } from "../../drafts/useWorkspaceDraft";
import { useBookDetail } from "../book/bookContext";
import { sampleAudioUrl } from "../book/review/useReview";
import { type EditableStyleSheet, editableContent, wireStyleSheet } from "./draft";
import { TermReach, useTermReach } from "./TermReach";

export function StyleSheetScreen() {
  const { t } = useTranslation("stylesheet");
  const book = useBookDetail();
  const query = useQuery({
    queryKey: queryKeys.styleSheet(book.id),
    queryFn: () => api<StyleSheet>(buildPath("/books/:id/style-sheet", { id: book.id })),
    enabled: book.styleSheet !== null,
    retry: false,
  });
  const currentVersion = book.styleSheet?.version;
  const loadedVersion = query.data?.version;
  useEffect(() => {
    if (
      currentVersion !== undefined &&
      loadedVersion !== undefined &&
      currentVersion !== loadedVersion
    )
      void query.refetch();
  }, [currentVersion, loadedVersion, query.refetch]);
  if (!book.styleSheet && book.failure) return <p className="text-ink-2">{t("creatingFailed")}</p>;
  if (!book.styleSheet || query.isPending)
    return (
      <div className="w-full space-y-4">
        <h1 className="font-serif text-title font-semibold">{t("title")}</h1>
        <Banner tone="working">{book.confirmedAt ? t("creating") : t("confirmFirst")}</Banner>
      </div>
    );
  if (!query.data)
    return (
      <ErrorBanner
        error={query.error}
        action={
          <Button variant="quiet" onClick={() => void query.refetch()}>
            {t("retry")}
          </Button>
        }
      />
    );
  return (
    <AudioProvider>
      <StyleEditor
        key={book.id}
        sheet={query.data}
        readError={query.error}
        onReload={() => void query.refetch()}
      />
    </AudioProvider>
  );
}

function StyleEditor({
  sheet,
  readError,
  onReload,
}: {
  sheet: StyleSheet;
  readError: unknown;
  onReload: () => void;
}) {
  const { t } = useTranslation("stylesheet");
  const book = useBookDetail();
  const client = useQueryClient();
  const initial = useMemo(() => editableContent(sheet), [sheet]);
  const workspace = useWorkspaceDraft(book.id, "guidance", initial);
  const draft = workspace.value;
  const setDraft = workspace.setValue;
  const editing = true;
  const [saved, setSaved] = useState(false);
  const [confirmRerun, setConfirmRerun] = useState(false);
  const [activeTerm, setActiveTerm] = useState<number | null>(null);
  const [sampleId, setSampleId] = useState<string | null>(null);
  const played = useRef<string | null>(null);
  const playback = usePlayback();
  const previewKey = JSON.stringify([wireStyleSheet(draft), sheet.checkVoice]);
  const currentPreview = useRef(previewKey);
  currentPreview.current = previewKey;
  const requestedPreview = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: each content/voice fingerprint invalidates the currently playing preview.
  useEffect(() => {
    playback.pause();
    setSampleId(null);
    setActiveTerm(null);
    played.current = null;
  }, [previewKey, playback.pause]);
  const display = draft;
  const wire = useMemo(() => wireStyleSheet(draft), [draft]);
  const reach = useTermReach(book.id, wire);
  const save = { isPending: workspace.applying || !workspace.loaded, error: workspace.error };
  const rerun = useMutation({
    mutationFn: () =>
      api<RerunUnapprovedResponse>(
        buildPath("/books/:id/style-sheet/rerun-unapproved", { id: book.id }),
        { method: "POST" },
      ),
    onSuccess: () => {
      setConfirmRerun(false);
      void client.invalidateQueries({ queryKey: queryKeys.book(book.id) });
      void client.invalidateQueries({ queryKey: queryKeys.styleSheet(book.id) });
    },
  });
  const check = useMutation({
    mutationFn: (termIndex: number) =>
      api<Sample>(
        buildPath("/books/:id/style-sheet/terms/:termId/check", {
          id: book.id,
          termId: String(termIndex),
        }),
        {
          method: "POST",
          body: {
            baseVersion: sheet.version,
            draft: wireStyleSheet(draft),
            voiceId: sheet.checkVoice?.voiceId,
          },
        },
      ),
    onSuccess: (sample) => {
      if (requestedPreview.current !== currentPreview.current) return;
      client.setQueryData(queryKeys.sample(sample.id), sample);
      setSampleId(sample.id);
    },
  });
  const sample = useQuery({
    queryKey: queryKeys.sample(sampleId ?? ""),
    queryFn: () => api<Sample>(buildPath("/samples/:id", { id: sampleId ?? "" })),
    enabled: sampleId !== null,
    retry: false,
    refetchInterval: (q) => (q.state.data?.status === "rendering" ? 1000 : false),
  });
  useEffect(() => {
    if (
      sample.data?.status === "cached" &&
      played.current !== sample.data.id &&
      requestedPreview.current === currentPreview.current
    ) {
      played.current = sample.data.id;
      playback.play(sample.data.id, sampleAudioUrl(sample.data.id));
    }
  }, [sample.data, playback.play]);
  const checkBusy = check.isPending || sample.data?.status === "rendering";
  const play = (index: number) => {
    if (checkBusy) return;
    if (index === activeTerm && playback.status === "playing") {
      playback.pause();
      return;
    }
    played.current = null;
    setSampleId(null);
    setActiveTerm(index);
    requestedPreview.current = previewKey;
    check.mutate(index);
  };
  const updateTerm = (index: number, patch: Partial<EditableStyleSheet["terms"][number]>) =>
    setDraft({
      ...draft,
      terms: draft.terms.map((term, at) => (at === index ? { ...term, ...patch } : term)),
    });
  return (
    <div className="grid min-w-0 grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      <section className="min-w-0 rounded-md border border-line bg-surface">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line px-5 py-5">
          <div>
            <h1 className="font-serif text-title font-semibold">{t("title")}</h1>
            <p className="mt-2 text-ink-2">{t("description", { version: sheet.version })}</p>
          </div>
        </header>
        <ErrorBanner
          error={save.error || readError}
          action={
            <Button variant="quiet" onClick={onReload}>
              {t("retry")}
            </Button>
          }
        />
        {workspace.dirty && sheet.version !== draft.baseVersion ? (
          <Banner tone="warn">{t("conflict")}</Banner>
        ) : null}
        {book.reviewPolicy === "exceptions" ? (
          <Banner tone="info">{t("qualityImpact", { count: book.counts.included })}</Banner>
        ) : null}
        {saved && !workspace.dirty ? (
          <Banner tone="info">
            {book.reviewPolicy === "exceptions" ? t("qualitySaved") : t("saved")}
          </Banner>
        ) : null}
        <div className="space-y-6 px-5 py-5">
          <div className="grid gap-4 md:grid-cols-2">
            <TextInput
              label={t("genre")}
              value={display.book.genre}
              readOnly={!editing}
              disabled={save.isPending}
              onChange={(e) =>
                setDraft({ ...draft, book: { ...draft.book, genre: e.target.value } })
              }
            />
            <TextInput
              label={t("tone")}
              value={display.book.tone}
              readOnly={!editing}
              disabled={save.isPending}
              onChange={(e) =>
                setDraft({ ...draft, book: { ...draft.book, tone: e.target.value } })
              }
            />
          </div>
          <section className="space-y-3">
            <h2 className="label-mono">{t("conventions")}</h2>
            {display.conventions.map((rule, index) => (
              <div
                key={rule.localId}
                className="grid gap-3 border-b border-line-soft pb-3 sm:grid-cols-[140px_minmax(0,1fr)]"
              >
                {editing ? (
                  <TextInput
                    aria-label={t("topic")}
                    value={rule.topic}
                    disabled={save.isPending}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        conventions: draft.conventions.map((row, at) =>
                          at === index ? { ...row, topic: e.target.value } : row,
                        ),
                      })
                    }
                  />
                ) : (
                  <p className="font-mono text-data text-ink-2">{rule.topic}</p>
                )}
                {editing ? (
                  <div className="space-y-2">
                    <TextArea
                      label={t("rule")}
                      value={rule.rule}
                      disabled={save.isPending}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          conventions: draft.conventions.map((row, at) =>
                            at === index ? { ...row, rule: e.target.value } : row,
                          ),
                        })
                      }
                    />
                    <Button
                      variant="quiet"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          conventions: draft.conventions.filter((_, at) => at !== index),
                        })
                      }
                    >
                      {t("remove")}
                    </Button>
                  </div>
                ) : (
                  <p>{rule.rule}</p>
                )}
              </div>
            ))}
            {editing ? (
              <Button
                variant="quiet"
                disabled={save.isPending}
                onClick={() =>
                  setDraft({
                    ...draft,
                    conventions: [
                      ...draft.conventions,
                      { topic: "", rule: "", localId: crypto.randomUUID() },
                    ],
                  })
                }
              >
                {t("addConvention")}
              </Button>
            ) : null}
          </section>
          <section className="space-y-3">
            <h2 className="label-mono">{t("terms")}</h2>
            <p className="text-ink-2">{t("termsHelp")}</p>
            <ErrorBanner error={check.error || sample.error} />
            {sample.data?.status === "failed" ? (
              <Banner tone="danger">{t("checkFailed")}</Banner>
            ) : null}
            {display.terms.length === 0 ? <p className="text-ink-3">{t("noTerms")}</p> : null}
            {display.terms.map((term, index) => (
              <div key={term.localId} className="space-y-3 border-b border-line-soft py-3">
                <div className="grid min-w-0 gap-3 md:grid-cols-3">
                  <TextInput
                    label={t("term")}
                    value={term.term}
                    readOnly={!editing}
                    disabled={save.isPending}
                    onChange={(e) => updateTerm(index, { term: e.target.value })}
                  />
                  <TextInput
                    label={t("sayAs")}
                    value={term.say_as}
                    readOnly={!editing}
                    disabled={save.isPending}
                    onChange={(e) => updateTerm(index, { say_as: e.target.value })}
                  />
                  <TextInput
                    label={t("ipa")}
                    value={term.ipa ?? ""}
                    readOnly={!editing}
                    disabled={save.isPending}
                    className="font-ipa"
                    onChange={(e) => updateTerm(index, { ipa: e.target.value || null })}
                  />
                </div>
                {editing ? (
                  <TextInput
                    label={t("note")}
                    value={term.note}
                    disabled={save.isPending}
                    onChange={(e) => updateTerm(index, { note: e.target.value })}
                  />
                ) : term.note ? (
                  <p className="text-data text-ink-2">{term.note}</p>
                ) : null}
                {term.forms.map((form, at) => (
                  <div
                    key={form.localId}
                    className="grid gap-2 border-l-2 border-line pl-3 md:grid-cols-3"
                  >
                    <TextInput
                      label={t("spelling")}
                      value={form.spelling}
                      readOnly={!editing}
                      disabled={save.isPending}
                      onChange={(e) =>
                        updateTerm(index, {
                          forms: term.forms.map((f, i) =>
                            i === at ? { ...f, spelling: e.target.value } : f,
                          ),
                        })
                      }
                    />
                    <TextInput
                      label={t("sayAs")}
                      value={form.say_as}
                      readOnly={!editing}
                      disabled={save.isPending}
                      onChange={(e) =>
                        updateTerm(index, {
                          forms: term.forms.map((f, i) =>
                            i === at ? { ...f, say_as: e.target.value } : f,
                          ),
                        })
                      }
                    />
                    <TextInput
                      label={t("ipa")}
                      value={form.ipa ?? ""}
                      readOnly={!editing}
                      disabled={save.isPending}
                      onChange={(e) =>
                        updateTerm(index, {
                          forms: term.forms.map((f, i) =>
                            i === at ? { ...f, ipa: e.target.value || null } : f,
                          ),
                        })
                      }
                    />
                    {editing ? (
                      <Button
                        variant="quiet"
                        size="sm"
                        disabled={save.isPending}
                        onClick={() =>
                          updateTerm(index, { forms: term.forms.filter((_, i) => i !== at) })
                        }
                      >
                        {t("removeForm")}
                      </Button>
                    ) : null}
                  </div>
                ))}
                <TermReach
                  book={book}
                  draft={wire}
                  termIndex={index}
                  reach={reach.data?.terms[index]}
                />
                {editing ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!sheet.checkVoice || checkBusy || save.isPending}
                      busy={activeTerm === index && checkBusy}
                      onClick={() => play(index)}
                    >
                      {t("nav:workspace.preview")}
                    </Button>
                    <Button
                      variant="quiet"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() =>
                        updateTerm(index, {
                          forms: [
                            ...term.forms,
                            { spelling: "", say_as: "", ipa: null, localId: crypto.randomUUID() },
                          ],
                        })
                      }
                    >
                      {t("addForm")}
                    </Button>
                    <Button
                      variant="quiet"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() =>
                        setDraft({ ...draft, terms: draft.terms.filter((_, at) => at !== index) })
                      }
                    >
                      {t("removeTerm")}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
            {editing ? (
              <Button
                variant="quiet"
                disabled={save.isPending}
                onClick={() =>
                  setDraft({
                    ...draft,
                    terms: [
                      ...draft.terms,
                      {
                        term: "",
                        say_as: "",
                        ipa: null,
                        note: "",
                        forms: [],
                        localId: crypto.randomUUID(),
                      },
                    ],
                  })
                }
              >
                {t("addTerm")}
              </Button>
            ) : null}
          </section>
          <DraftActions
            draft={workspace}
            label={t("nav:workspace.applyGuidance")}
            disabled={!guidanceCanApply(draft)}
            onApply={() =>
              void workspace
                .apply()
                .then(() => setSaved(true))
                .catch(() => {})
            }
          >
            {!guidanceCanApply(draft) ? <p className="text-danger">{t("incomplete")}</p> : null}
            <p>
              {book.reviewPolicy === "exceptions"
                ? t("qualityImpact", { count: book.counts.included })
                : t("manualImpact")}
            </p>
          </DraftActions>
        </div>
      </section>
      <aside className="min-w-0 space-y-4">
        <Card label={t("instructions")}>
          <div className="space-y-3">
            {sheet.instructions.length === 0 ? (
              <p className="text-ink-3">{t("noInstructions")}</p>
            ) : (
              sheet.instructions.map((instruction) => (
                <div key={instruction.id}>
                  <p>{instruction.text}</p>
                  {instruction.chapterNumber !== null ? (
                    <p className="mt-1 font-mono text-data text-ink-3">
                      {t("instructionFrom", { chapter: instruction.chapterNumber })}
                    </p>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </Card>
        {sheet.unapprovedChapterCount > 0 || rerun.isSuccess ? (
          <Card title={t("changed")} titleSize="sm" tone="warn">
            <p className="mb-3 text-ink-2">
              {t("rerunHelp", { count: sheet.unapprovedChapterCount })}
            </p>
            <Button
              variant="quiet"
              disabled={workspace.dirty || sheet.unapprovedChapterCount === 0 || rerun.isPending}
              onClick={() => setConfirmRerun(true)}
            >
              {t("rerun", { count: sheet.unapprovedChapterCount })}
            </Button>
            <ErrorBanner error={rerun.error} />
            {rerun.isSuccess ? (
              <p role="status" className="mt-2 text-ok">
                {t("rerunStarted")}
              </p>
            ) : null}
          </Card>
        ) : null}
        <Card label={t("voice")}>
          <p>{sheet.checkVoice?.name ?? t("noVoice")}</p>
          <p className="mt-2 text-ink-2">{t("voiceHelp")}</p>
        </Card>
        {book.chapters.some(
          (chapter) => chapter.id === book.calibrationChapterId && chapter.versionCount > 0,
        ) ? (
          <Link
            className="inline-block text-accent underline"
            to={`/books/${book.id}/review/${book.calibrationChapterId}`}
          >
            {t("reviewCalibration")}
          </Link>
        ) : null}
      </aside>
      <ConfirmDialog
        open={confirmRerun}
        title={t("rerunTitle")}
        confirmLabel={t("rerun", { count: sheet.unapprovedChapterCount })}
        busy={rerun.isPending}
        onConfirm={() => rerun.mutate()}
        onCancel={() => setConfirmRerun(false)}
      >
        <p>{t("rerunConfirm")}</p>
      </ConfirmDialog>
    </div>
  );
}
